import parse from 'html-react-parser';
import { useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { JobPost } from '~/api/jobPosts/types';
import type { EditorRefHandle } from '~/components';

import { useApplyInternalJobPost } from '~/api/jobPosts/useApplyInternalJobPost';
import { useUser } from '~/api/user';
import { Button, Card, Dialog, ExternalLink, RichTextEditor, Text, Title } from '~/components';
import { Checkbox, Field, Form } from '~/components/Form';
import { useCommunityProfileLinks } from '~/features/community';
import { parsePhoneNumber } from '~/utils/libphonenumberjs';
import { toast } from '~/utils/toast';

import type { JobPostApplyForm } from './types';

import {
  actionsStyle,
  checkboxLabelStyle,
  formWrapperStyle,
  optionalStyle,
  profileCardStyle,
  profileGridStyle,
  profileIntroductionRowStyle,
  profileIntroductionStyle,
  profileLinksStyle,
  profileSectionStyle,
  profileSectionTitleStyle,
  sectionExplanationStyle,
  sectionStyle,
} from './JobPostApplyDialog.css';

type Props = {
  isOpen: boolean;
  jobPost: JobPost & { applyMode: 'InternalEmail' };
  onClose: (success: boolean) => void;
};

export const JobPostApplyDialog = ({ isOpen, jobPost, onClose }: Props) => {
  const { t } = useTranslation(['jobPosts']);

  return (
    <Dialog.Root onOpenChange={(open) => !open && onClose(false)} open={isOpen}>
      <Dialog.Body>
        <Dialog.Header title={`${t('jobPosts:apply.title')} ${jobPost.clientName}`} />

        <JobPostApplyDialogContent jobPost={jobPost} onClose={onClose} />
      </Dialog.Body>
    </Dialog.Root>
  );
};

const JobPostApplyDialogContent = ({ jobPost, onClose }: Omit<Props, 'isOpen'>) => {
  const { data: user } = useUser();
  const applyMutation = useApplyInternalJobPost(jobPost.id);

  const filteredLinks = useCommunityProfileLinks(user.communityProfile.links);

  const { t } = useTranslation(['common', 'jobPosts']);
  const editorRef = useRef<EditorRefHandle>(null);

  const introduction = user.communityProfile.description;

  const form = useForm<JobPostApplyForm>({
    defaultValues: {
      motivation: '',
      sharePhone: false,
      shareLocation: false,
      shareIntroduction: false,
    },
  });
  const { control, handleSubmit } = form;

  const onSubmit = handleSubmit((data) => {
    applyMutation.mutate(
      {
        introduction: data.shareIntroduction ? user.communityProfile.description : null,
        motivation: data.motivation,
        sharePhone: data.sharePhone,
        shareLocation: data.shareLocation,
      },
      {
        onSuccess: () => onClose(true),
        onError: () => toast.danger({ description: t('jobPosts:alerts.errorApplied') }),
      },
    );
  });

  return (
    <Form onSubmit={onSubmit}>
      <div className={formWrapperStyle}>
        {/* Profile */}
        <section className={sectionStyle}>
          <Title as="h3" styleAs="h4">
            {t('jobPosts:apply.profile.title')}
          </Title>

          <Text className={sectionExplanationStyle}>{t('jobPosts:apply.profile.explanation')}</Text>

          <Card.Root className={profileCardStyle} variant="primary-light">
            <div className={profileSectionStyle}>
              <Text className={profileSectionTitleStyle}>{t('jobPosts:apply.profile.contactDetails')}</Text>

              <div className={profileGridStyle}>
                <Field.Root>
                  <Checkbox.Root checked disabled>
                    <Checkbox.Label className={checkboxLabelStyle}>{t('jobPosts:apply.profile.fields.email')}</Checkbox.Label>

                    <Checkbox.Description>{user.communityProfile.email}</Checkbox.Description>
                  </Checkbox.Root>
                </Field.Root>

                {filteredLinks.length > 0 && (
                  <Field.Root>
                    <Checkbox.Root checked disabled>
                      <Checkbox.Label className={checkboxLabelStyle}>{t('jobPosts:apply.profile.fields.links')}</Checkbox.Label>

                      <Checkbox.Description className={profileLinksStyle}>
                        {filteredLinks.map(({ type, url }) => (
                          <ExternalLink href={/^https?:\/\//i.test(url) ? url : `https://${url}`} key={type}>
                            {url}
                          </ExternalLink>
                        ))}
                      </Checkbox.Description>
                    </Checkbox.Root>
                  </Field.Root>
                )}

                {/* Location */}
                <Controller
                  control={control}
                  name="shareLocation"
                  render={({ field: { name, onBlur, onChange, value } }) => (
                    <Field.Root name={name}>
                      <Checkbox.Root checked={value} onBlur={onBlur} onCheckedChange={onChange}>
                        <Checkbox.Label className={checkboxLabelStyle}>{t('jobPosts:apply.profile.fields.address')}</Checkbox.Label>

                        <Checkbox.Description>
                          {user.address.street} {user.address.number} – {user.address.zipCode} {user.address.city}
                        </Checkbox.Description>
                      </Checkbox.Root>
                    </Field.Root>
                  )}
                />

                {/* Phone */}
                <Controller
                  control={control}
                  name="sharePhone"
                  render={({ field: { name, onBlur, onChange, value } }) => (
                    <Field.Root name={name}>
                      <Checkbox.Root checked={value} onBlur={onBlur} onCheckedChange={onChange}>
                        <Checkbox.Label className={checkboxLabelStyle}>{t('jobPosts:apply.profile.fields.phone')}</Checkbox.Label>

                        <Checkbox.Description>{parsePhoneNumber(user.phone)?.format('INTERNATIONAL') || user.phone}</Checkbox.Description>
                      </Checkbox.Root>
                    </Field.Root>
                  )}
                />

                {introduction && (
                  <Controller
                    control={control}
                    name="shareIntroduction"
                    render={({ field: { name, onBlur, onChange, value } }) => (
                      <Field.Root className={profileIntroductionRowStyle} name={name}>
                        <Checkbox.Root checked={value} onBlur={onBlur} onCheckedChange={onChange}>
                          <Checkbox.Label className={checkboxLabelStyle}>{t('jobPosts:apply.profile.introductionText')}</Checkbox.Label>

                          <Checkbox.Description className={profileIntroductionStyle}>{parse(introduction)}</Checkbox.Description>
                        </Checkbox.Root>
                      </Field.Root>
                    )}
                  />
                )}
              </div>
            </div>
          </Card.Root>
        </section>

        {/* Motivation */}
        <section className={sectionStyle}>
          <Controller
            control={control}
            name="motivation"
            render={({ field: { name, onChange, value }, fieldState: { invalid } }) => (
              <Field.Root name={name}>
                <Field.Label>
                  {t('jobPosts:apply.motivation.title')} <span className={optionalStyle}>{t('common:optionalInput')}</span>
                </Field.Label>

                <Field.Description className={sectionExplanationStyle}>{t('jobPosts:apply.motivation.explanation')}</Field.Description>

                <RichTextEditor isInvalid={invalid} mode="html" onChange={onChange} ref={editorRef} value={value} />
              </Field.Root>
            )}
          />
        </section>
      </div>

      {/* Submit */}
      <Dialog.Actions className={actionsStyle}>
        <Button icon="Send" loading={applyMutation.isPending} type="submit">
          {t(`jobPosts:apply.actions.submit`)}
        </Button>
      </Dialog.Actions>
    </Form>
  );
};
